/* eslint-disable max-lines-per-function, max-statements */
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { AppStoreRestrictionModal } from '../components/AppStoreRestrictionModal';
import { UpgradeModal } from '../components/BillingModal';
import {
  getDeployErrorMessage,
  isFreeTier,
  submitStageDeploy,
} from '../components/DeployStageButton';
import { DesignPagePickerModal } from '../components/DesignPagePickerModal';
import { ModalBackdrop } from '../components/ModalBackdrop';
import { ModalContent } from '../components/ModalContent';
import { ModalHeader } from '../components/ModalHeader';
import { ShareModal } from '../components/ShareModal';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';
import { errorMessage } from '../services/apiError';
import type { DesignPage } from '../services/designService';
import { projectService } from '../services/projectService';
import { useGetDesignPagesQuery } from '../store/services/designApi';
import type { SupervisorButton, SupervisorButtonAction } from '../types/supervisorButtons';
import { getCompletedEditorPages } from '../utils/designEditorAction';
import { isIOSApp } from '../utils/platformDetection';

const PROJECT_UTILITY_ACTIONS = new Set<SupervisorButtonAction>([
  'trigger_export_proposal',
  'open_share_modal',
  'open_design_editor',
  'trigger_stage_deploy',
  'resume_quota_build',
]);

export const isProjectUtilityAction = (
  action: SupervisorButtonAction | undefined,
): action is SupervisorButtonAction => Boolean(action && PROJECT_UTILITY_ACTIONS.has(action));

function DesignRequiredModal({
  onClose,
  purpose,
}: {
  onClose: () => void;
  purpose: 'sharing' | 'editing' | 'deploying';
}) {
  const label = purpose === 'sharing'
    ? 'sharing this product'
    : purpose === 'editing'
      ? 'editing this product'
      : 'deploying this product to stage';
  const title = purpose === 'editing'
    ? 'Design editing is not ready yet'
    : 'First create at least one design';

  return createPortal(
    <ModalBackdrop isVisible onClick={onClose}>
      <ModalContent onClose={onClose}>
        <ModalHeader
          title={title}
          subtitle={`Finish generating one editable design page before ${label}.`}
          onClose={onClose}
        />
      </ModalContent>
    </ModalBackdrop>,
    document.body,
  );
}

export function useProjectActionButtonHandlers(projectId?: string | null) {
  const toast = useToast();
  const { user } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [designRequiredPurpose, setDesignRequiredPurpose] = useState<'sharing' | 'editing' | 'deploying' | null>(null);
  const [editorPageChoices, setEditorPageChoices] = useState<DesignPage[]>([]);
  const [iosRestrictionOpen, setIosRestrictionOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const { currentData: designResponse, refetch: refetchDesignPages } = useGetDesignPagesQuery(
    { projectId: projectId || '', light: true, includePlanned: true },
    { skip: !projectId },
  );
  const hasKnownDesignPage = useMemo(() => {
    if (!designResponse) return true;

    return designResponse.design_pages.some((page) => (
      Boolean(page.id)
      && (
        Boolean(page.compiled_html)
        || Boolean(page.jsx_content)
        || Boolean(page.has_compiled_html)
        || Boolean(page.has_jsx_content)
        || Boolean(page.page_name)
      )
    ));
  }, [designResponse]);

  const handleExport = useCallback(() => {
    window.dispatchEvent(new globalThis.CustomEvent('trigger_export_proposal', {
      detail: { source: 'structured_action_button' },
    }));
  }, []);

  const handleShare = useCallback(() => {
    if (!projectId) return;
    if (!hasKnownDesignPage) {
      setDesignRequiredPurpose('sharing');

      return;
    }
    setShareOpen(true);
  }, [hasKnownDesignPage, projectId]);

  const handleEditDesign = useCallback(async () => {
    if (!projectId) return;
    let response = designResponse;

    if (!response) {
      try {
        response = (await refetchDesignPages()).data;
      } catch {
        response = undefined;
      }
    }

    const completedPages = getCompletedEditorPages(response?.design_pages);

    if (completedPages.length === 0) {
      setDesignRequiredPurpose('editing');

      return;
    }

    if (completedPages.length > 1) {
      setEditorPageChoices(completedPages);

      return;
    }

    window.dispatchEvent(new globalThis.CustomEvent('open_design_editor', {
      detail: { pageName: completedPages[0].page_name },
    }));
  }, [designResponse, projectId, refetchDesignPages]);

  const handleDeploy = useCallback(async () => {
    if (!projectId || deploying) return;
    if (isIOSApp()) {
      setIosRestrictionOpen(true);

      return;
    }
    if (isFreeTier(user?.subscription_tier)) {
      setUpgradeOpen(true);

      return;
    }
    if (!hasKnownDesignPage) {
      setDesignRequiredPurpose('deploying');

      return;
    }

    setDeploying(true);
    try {
      await submitStageDeploy(projectId, toast);
    } catch (error) {
      toast.error(getDeployErrorMessage(error));
    } finally {
      setDeploying(false);
    }
  }, [deploying, hasKnownDesignPage, projectId, toast, user?.subscription_tier]);

  // Resume build on a build held for the plan's hourly quota: nothing resumes it by itself.
  const handleResumeHeldBuild = useCallback(async () => {
    if (!projectId) return;
    try {
      const { resumed } = await projectService.resumeHeldBuild(projectId);

      // Still spent: the chat answers with the queue notice again, with the new wait.
      if (resumed) toast.success('Build resumed.');
    } catch (error) {
      toast.error(errorMessage(error, 'We couldn’t resume the build. Refresh and try again.'));
    }
  }, [projectId, toast]);

  const handleProjectActionButton = useCallback(async (button: SupervisorButton) => {
    if (button.action === 'trigger_export_proposal') {
      handleExport();

      return;
    }
    if (button.action === 'open_share_modal') {
      handleShare();

      return;
    }
    if (button.action === 'open_design_editor') {
      await handleEditDesign();

      return;
    }
    if (button.action === 'trigger_stage_deploy') {
      await handleDeploy();

      return;
    }
    if (button.action === 'resume_quota_build') await handleResumeHeldBuild();
  }, [handleDeploy, handleEditDesign, handleExport, handleResumeHeldBuild, handleShare]);

  const modals = (
    <>
      {shareOpen && projectId && (
        <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} projectId={projectId} />
      )}
      {designRequiredPurpose && (
        <DesignRequiredModal
          onClose={() => setDesignRequiredPurpose(null)}
          purpose={designRequiredPurpose}
        />
      )}
      {editorPageChoices.length > 1 && (
        <DesignPagePickerModal
          pages={editorPageChoices}
          onClose={() => setEditorPageChoices([])}
          onSelect={(pageName) => {
            setEditorPageChoices([]);
            window.dispatchEvent(new globalThis.CustomEvent('open_design_editor', {
              detail: { pageName },
            }));
          }}
        />
      )}
      <AppStoreRestrictionModal
        isOpen={iosRestrictionOpen}
        onClose={() => setIosRestrictionOpen(false)}
      />
      {upgradeOpen && <UpgradeModal onClose={() => setUpgradeOpen(false)} />}
    </>
  );

  return {
    deploying,
    handleProjectActionButton,
    modals,
  };
}
